import { fetchCurrentUserRoles, Role } from 'libs/api'
import React, { ReactNode, useCallback, useEffect, useState } from 'react'
import { CognitoUser } from 'types'
import PubSub from 'pubsub-js'
import { queryClient, useQuery } from 'hooks'
import ErrorState from './State/ErrorState'
import Analytics from '@aws-amplify/analytics'
import { initializeApp } from 'firebase/app'
import { getMessaging, getToken } from 'firebase/messaging'

import { AuthContext, AuthContextType } from './contexts'

export { AuthContext } from './contexts'
export type { AuthContextType } from './contexts'

const firebaseConfig = {
  apiKey: 'AIzaSyDBxZxyFiD9bTz0_L4LBY6DePa00OK9oNE',
  authDomain: 'keee-web.firebaseapp.com',
  projectId: 'keee-web',
  storageBucket: 'keee-web.appspot.com',
  messagingSenderId: '702265651801',
  appId: '1:702265651801:web:9bca0079d65812d12c7748',
  measurementId: 'G-TPQ4DL8NKS',
}

const app = initializeApp(firebaseConfig)
const messaging = getMessaging(app)

type Props = Pick<AuthContextType, 'currentAuthenticatedUser' | 'setCurrentAuthenticatedUser'> & {
  children: ReactNode
}

const currentRoleIdKey = 'currentRoleId'
const userRolesInitialData: Role[] = JSON.parse(localStorage.getItem('fetchCurrentUserRoles') ?? '[]')

let refreshPromise: Promise<unknown>

export async function getCurrentRoleHeader() {
  await refreshPromise
  return localStorage.getItem(currentRoleIdKey) ?? ''
}

export const AuthContextProvider = ({ children, currentAuthenticatedUser, setCurrentAuthenticatedUser }: Props) => {
  const [challengeUser, setChallengeUser] = useState<CognitoUser>()
  const [resetPasswordUsername, setResetPasswordUsername] = useState<string>()
  const [currentRole, _setCurrentRole] = useState<Role>()
  const {
    data: userRoles,
    refetch,
    error,
  } = useQuery(
    fetchCurrentUserRoles,
    {},
    {
      enabled: Boolean(currentAuthenticatedUser),
      // Initialize with the stored values, but refresh immediately:
      staleTime: 0,
      initialData: userRolesInitialData,
    }
  )

  const notifPublicKey = 'BEhNuCgfVDFB2cqK5Wjn8gYWrC8OGobCmkegjd9JqIBRAYbuX_2WRDpGVoXOkM0wEWlHzfGtEvXM3NN3i59A2Wk'

  function sendTokenToPinpoint(user: any, fcmToken: string) {
    try {
      const result = Analytics.updateEndpoint({
        address: fcmToken,
        channelType: 'GCM',
        optOut: 'NONE',
        userId: user.username,
        userAttributes: {
          email: [user.attributes.email],
        },
        attributes: {
          roles: user.attributes['custom:role'] ? user.attributes['custom:role'].split(',') : [],
          locationIds: user.attributes['custom:location_ids'] ? user.attributes['custom:location_ids'].split(',') : [],
        },
      })
      console.log('Pinpoint endpoint registered successfully:', result)
    } catch (error) {
      console.error('Failed to register push notification endpoint with Pinpoint:', error)
      console.error('User details - username:', user.username, 'email:', user.attributes.email)
    }
  }

  const setCurrentRole = useCallback((role?: Role) => {
    localStorage.setItem(currentRoleIdKey, role?.role_id ?? '')
    _setCurrentRole((oldRole) => {
      if (oldRole) setTimeout(() => queryClient.invalidateQueries(), 10)
      return role
    })
  }, [])

  useEffect(() => {
    const token = PubSub.subscribe('currentRoleError', () => {
      setCurrentRole(undefined)
      if (userRoles) refreshPromise = refetch()
    })

    // Return cleanup function that unsubscribes
    return () => {
      PubSub.unsubscribe(token)
    }
  }, [refetch, setCurrentRole, userRoles])

  useEffect(() => {
    if (!currentAuthenticatedUser) return
    const tenantId = currentAuthenticatedUser.attributes['custom:tenant_id']
    if (tenantId) {
      localStorage.setItem('tenantId', tenantId)
    } else {
      localStorage.removeItem('tenantId')
    }

    if (!userRoles) return
    function requestPermission() {
      if (!('Notification' in window)) {
        console.log('This browser does not support notifications.')
        return
      }

      console.log('Requesting permission...')
      Notification?.requestPermission?.().then((permission) => {
        if (permission === 'granted') {
          console.log('Notification permission granted.')
        }
      })
    }

    requestPermission()

    getToken(messaging, { vapidKey: notifPublicKey }).then((token: string) => {
      sendTokenToPinpoint(currentAuthenticatedUser, token)
    })

    localStorage.setItem('fetchCurrentUserRoles', JSON.stringify(userRoles))
    const storedRoleId = localStorage.getItem(currentRoleIdKey)
    const currentRole = userRoles.find((role) => role.role_id === storedRoleId) ?? userRoles[0]
    setCurrentRole(currentRole)
  }, [currentAuthenticatedUser, setCurrentRole, setCurrentAuthenticatedUser, userRoles, refetch])

  if (error) return <ErrorState retry={refetch} />

  const authProps: AuthContextType = {
    currentAuthenticatedUser,
    setCurrentAuthenticatedUser,
    challengeUser,
    setChallengeUser,
    resetPasswordUsername,
    setResetPasswordUsername,
    currentRole,
    setCurrentRole,
  }

  return <AuthContext.Provider value={authProps}>{children}</AuthContext.Provider>
}
